"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Who sees it",
    rule: "Settings → Preferences and advanced → Additional charges. Title Additional Charges Configuration. View permission (or admin) required. Add Additional Charge needs add permission",
  },
  {
    topic: "Tax Rate",
    rule: "When isTaxable is on, Tax Rate in (%) shows 18 and is disabled — you do not type a different rate here",
  },
  {
    topic: "Delete",
    rule: "Confirm Are you sure you want to delete this charge?",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. The list" },
      { heading: "2. Add / Edit Setup Charge" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function AdditionalChargesPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Additional Charges
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins (and roles with this permission) — named charges you
            can apply on selected documents
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Title <B>Additional Charges Configuration</B>. Subtitle: Configure
            additional charges that can be applied to various modules like
            quotes, invoices, and sales orders.
          </p>
          <Flow
            steps={[
              "Settings",
              "Additional charges",
              "Add Additional Charge",
              "Save",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The list
          </h3>
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "Name, Modules (tags), Taxable, Tax in (%), Actions (Edit / Delete)",
              },
              {
                term: "What it affects",
                desc: "Which named charges appear on the document types you tick. It does not set a default amount on the charge itself",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Add / Edit Setup Charge
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Modal <B>Add Setup Charge</B> or <B>Edit Setup Charge</B>.
          </p>
          <Steps
            items={[
              <>
                Enter <B>Charge Name</B> (required). Pick one or more{" "}
                <B>Modules</B> (required): Quote, Sales Order, Delivery
                Challan (if on), Invoice, Credit Note, Purchase Order, GRPO
                (if on), Bill, Debit Note, Recurring Invoice, Recurring Bill.
              </>,
              <>
                Tick <B>isActive</B> and optionally <B>isTaxable</B> (rate
                locks at 18%). Click <B>Add Setup Charge</B> or{" "}
                <B>Update Setup Charge</B>.
              </>,
            ]}
          />
          <Callout type="note">
            The checkboxes are labelled isActive and isTaxable on the form.
            The table only shows Taxable, not an Active column.
          </Callout>
          <Screenshot caption="Additional Charges Configuration — list and Add Setup Charge modal" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/discounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Discounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Company discount on / off — not these named charges
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              One of the modules you can attach a charge to
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
